import { Nullable } from "babylonjs/types";
import { serializeAsTexture, serialize, expandToProperty, serializeAsColor3, SerializationHelper } from "babylonjs/Misc/decorators";
import { Matrix } from "babylonjs/Maths/math.vector";
import { Color3 } from "babylonjs/Maths/math.color";
import { Tags } from "babylonjs/Misc/tags";
import { BaseTexture } from "babylonjs/Materials/Textures/baseTexture";
import { Texture } from "babylonjs/Materials/Textures/texture";
import { MaterialDefines } from "babylonjs/Materials/materialDefines";
import { MaterialHelper } from "babylonjs/Materials/materialHelper";
import { PushMaterial } from "babylonjs/Materials/pushMaterial";
import { MaterialFlags } from "babylonjs/Materials/materialFlags";
import { VertexBuffer } from "babylonjs/Meshes/buffer";
import { AbstractMesh } from "babylonjs/Meshes/abstractMesh";
import { SubMesh } from "babylonjs/Meshes/subMesh";
import { Mesh } from "babylonjs/Meshes/mesh";
import { Scene } from "babylonjs/scene";
import { _TypeStore } from 'babylonjs/Misc/typeStore';
import { IAnimatable } from 'babylonjs/Animations/animatable.interface';

import "./fire.fragment";
import "./fire.vertex";
import { EffectFallbacks } from 'babylonjs/Materials/effectFallbacks';

class FireMaterialDefines extends MaterialDefines {
    public DIFFUSE = false;
    public CLIPPLANE = false;
    public CLIPPLANE2 = false;
    public CLIPPLANE3 = false;
    public CLIPPLANE4 = false;
    public CLIPPLANE5 = false;
    public CLIPPLANE6 = false;
    public ALPHATEST = false;
    public DEPTHPREPASS = false;
    public POINTSIZE = false;
    public FOG = false;
    public UV1 = false;
    public VERTEXCOLOR = false;
    public VERTEXALPHA = false;
    public BonesPerMesh = 0;
    public NUM_BONE_INFLUENCERS = 0;
    public INSTANCES = false;
    public IMAGEPROCESSINGPOSTPROCESS = false;

    constructor() {
        super();
        this.rebuild();
    }
}

export class FireMaterial extends PushMaterial {
    @serializeAsTexture("diffuseTexture")
    private _diffuseTexture: Nullable<BaseTexture>;
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public diffuseTexture: Nullable<BaseTexture>;

    @serializeAsTexture("distortionTexture")
    private _distortionTexture: Nullable<BaseTexture>;
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public distortionTexture: Nullable<BaseTexture>;

    @serializeAsTexture("opacityTexture")
    private _opacityTexture: Nullable<BaseTexture>;
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public opacityTexture: Nullable<BaseTexture>;

    @serializeAsColor3("diffuse")
    public diffuseColor = new Color3(1, 1, 1);

    @serialize()
    public speed = 1.0;

    private _scaledDiffuse = new Color3();
    private _lastTime: number = 0;

    constructor(name: string, scene: Scene) {
        super(name, scene);
    }

    public needAlphaBlending(): boolean {
        return false;
    }

    public needAlphaTesting(): boolean {
        return true;
    }

    public getAlphaTestTexture(): Nullable<BaseTexture> {
        return null;
    }

    // Methods
    public isReadyForSubMesh(mesh: AbstractMesh, subMesh: SubMesh, useInstances?: boolean): boolean {
        if (this.isFrozen) {
            if (subMesh.effect && subMesh.effect._wasPreviouslyReady) {
                return true;
            }
        }

        if (!subMesh._materialDefines) {
            subMesh._materialDefines = new FireMaterialDefines();
        }

        var defines = <FireMaterialDefines>subMesh._materialDefines;
        var scene = this.getScene();

        if (this._isReadyForSubMesh(subMesh)) {
            return true;
        }

        var engine = scene.getEngine();

        // Textures
        if (defines._areTexturesDirty) {
            defines._needUVs = false;
            if (this._diffuseTexture && MaterialFlags.DiffuseTextureEnabled) {
                if (!this._diffuseTexture.isReady()) {
                    return false;
                } else {
                    defines._needUVs = true;
                    defines.DIFFUSE = true;
                }
            }
        }

        defines.ALPHATEST = this._opacityTexture ? true : false;

        // Misc.
        if (defines._areMiscDirty) {
            defines.POINTSIZE = (this.pointsCloud || scene.forcePointsCloud);
            defines.FOG = (scene.fogEnabled && mesh.applyFog && scene.fogMode !== Scene.FOGMODE_NONE && this.fogEnabled);
        }

        // Values that need to be evaluated on every frame
        MaterialHelper.PrepareDefinesForFrameBoundValues(scene, engine, defines, useInstances ? true : false);

        // Attribs
        MaterialHelper.PrepareDefinesForAttributes(mesh, defines, false, true);

        // Get correct effect
        if (defines.isDirty) {
            defines.markAsProcessed();

            scene.resetCachedMaterial();

            // Fallbacks
            var fallbacks = new EffectFallbacks();
            if (defines.FOG) {
                fallbacks.addFallback(1, "FOG");
            }

            if (defines.NUM_BONE_INFLUENCERS > 0) {
                fallbacks.addCPUSkinningFallback(0, mesh);
            }

            defines.IMAGEPROCESSINGPOSTPROCESS = scene.imageProcessingConfiguration.applyByPostProcess;

            //Attributes
            var attribs = [VertexBuffer.PositionKind];

            if (defines.UV1) {
                attribs.push(VertexBuffer.UVKind);
            }

            if (defines.VERTEXCOLOR) {
                attribs.push(VertexBuffer.ColorKind);
            }

            MaterialHelper.PrepareAttributesForBones(attribs, mesh, defines, fallbacks);
            MaterialHelper.PrepareAttributesForInstances(attribs, defines);

            // Legacy browser patch
            var shaderName = "fire";

            var join = defines.toString();
            subMesh.setEffect(scene.getEngine().createEffect(shaderName,
                {
                    attributes: attribs,
                    uniformsNames: ["world", "view", "viewProjection", "vEyePosition",
                        "vFogInfos", "vFogColor", "pointSize",
                        "vDiffuseInfos",
                        "mBones",
                        "vClipPlane", "vClipPlane2", "vClipPlane3", "vClipPlane4", "vClipPlane5", "vClipPlane6", "diffuseMatrix",
                        // Fire
                        "time", "speed"
                    ],
                    uniformBuffersNames: [],
                    samplers: ["diffuseSampler",
                        // Fire
                        "distortionSampler", "opacitySampler"
                    ],
                    defines: join,
                    fallbacks: fallbacks,
                    onCompiled: this.onCompiled,
                    onError: this.onError,
                    indexParameters: null,
                    maxSimultaneousLights: 4,
                    transformFeedbackVaryings: null
                }, engine), defines);
        }

        if (!subMesh.effect || !subMesh.effect.isReady()) {
            return false;
        }

        defines._renderId = scene.getRenderId();
        subMesh.effect._wasPreviouslyReady = true;

        return true;
    }

    public bindForSubMesh(world: Matrix, mesh: Mesh, subMesh: SubMesh): void {
        var scene = this.getScene();

        var defines = <FireMaterialDefines>subMesh._materialDefines;
        if (!defines) {
            return;
        }

        var effect = subMesh.effect;
        if (!effect) {
            return;
        }
        this._activeEffect = effect;

        // Matrices
        this.bindOnlyWorldMatrix(world);
        this._activeEffect.setMatrix("viewProjection", scene.getTransformMatrix());

        // Bones
        MaterialHelper.BindBonesParameters(mesh, this._activeEffect);

        if (this._mustRebind(scene, effect)) {
            // Textures
            if (this._diffuseTexture && MaterialFlags.DiffuseTextureEnabled) {
                this._activeEffect.setTexture("diffuseSampler", this._diffuseTexture);

                this._activeEffect.setFloat2("vDiffuseInfos", this._diffuseTexture.coordinatesIndex, this._diffuseTexture.level);
                this._activeEffect.setMatrix("diffuseMatrix", this._diffuseTexture.getTextureMatrix());

                this._activeEffect.setTexture("distortionSampler", this._distortionTexture);
                this._activeEffect.setTexture("opacitySampler", this._opacityTexture);
            }

            // Clip plane
            MaterialHelper.BindClipPlane(this._activeEffect, scene);

            // Point size
            if (this.pointsCloud) {
                this._activeEffect.setFloat("pointSize", this.pointSize);
            }

            MaterialHelper.BindEyePosition(effect, scene);
        }

        this._activeEffect.setColor4("vDiffuseColor", this._scaledDiffuse, this.alpha * mesh.visibility);

        // View
        if (scene.fogEnabled && mesh.applyFog && scene.fogMode !== Scene.FOGMODE_NONE) {
            this._activeEffect.setMatrix("view", scene.getViewMatrix());
        }

        // Fog
        MaterialHelper.BindFogParameters(scene, mesh, this._activeEffect);

        // Time
        this._lastTime += scene.getEngine().getDeltaTime();
        this._activeEffect.setFloat("time", this._lastTime);

        // Speed
        this._activeEffect.setFloat("speed", this.speed);

        this._afterBind(mesh, this._activeEffect);
    }

    public getAnimatables(): IAnimatable[] {
        var results = [];

        if (this._diffuseTexture && this._diffuseTexture.animations && this._diffuseTexture.animations.length > 0) {
            results.push(this._diffuseTexture);
        }
        if (this._distortionTexture && this._distortionTexture.animations && this._distortionTexture.animations.length > 0) {
            results.push(this._distortionTexture);
        }
        if (this._opacityTexture && this._opacityTexture.animations && this._opacityTexture.animations.length > 0) {
            results.push(this._opacityTexture);
        }

        return results;
    }

    public getActiveTextures(): BaseTexture[] {
        var activeTextures = super.getActiveTextures();

        if (this._diffuseTexture) {
            activeTextures.push(this._diffuseTexture);
        }

        if (this._distortionTexture) {
            activeTextures.push(this._distortionTexture);
        }

        if (this._opacityTexture) {
            activeTextures.push(this._opacityTexture);
        }

        return activeTextures;
    }

    public hasTexture(texture: BaseTexture): boolean {
        if (super.hasTexture(texture)) {
            return true;
        }

        if (this._diffuseTexture === texture) {
            return true;
        }

        if (this._distortionTexture === texture) {
            return true;
        }

        if (this._opacityTexture === texture) {
            return true;
        }

        return false;
    }

    public getClassName(): string {
        return "FireMaterial";
    }

    public dispose(forceDisposeEffect?: boolean): void {
        if (this._diffuseTexture) {
            this._diffuseTexture.dispose();
        }
        if (this._distortionTexture) {
            this._distortionTexture.dispose();
        }

        super.dispose(forceDisposeEffect);
    }

    public clone(name: string): FireMaterial {
        return SerializationHelper.Clone<FireMaterial>(() => new FireMaterial(name, this.getScene()), this);
    }

    public serialize(): any {

        var serializationObject = super.serialize();
        serializationObject.customType = "BABYLON.FireMaterial";
        serializationObject.diffuseColor = this.diffuseColor.asArray();
        serializationObject.speed = this.speed;

        if (this._diffuseTexture) {
            serializationObject._diffuseTexture = this._diffuseTexture.serialize();
        }

        if (this._distortionTexture) {
            serializationObject._distortionTexture = this._distortionTexture.serialize();
        }

        if (this._opacityTexture) {
            serializationObject._opacityTexture = this._opacityTexture.serialize();
        }

        return serializationObject;
    }

    public static Parse(source: any, scene: Scene, rootUrl: string): FireMaterial {
        var material = new FireMaterial(source.name, scene);

        material.diffuseColor = Color3.FromArray(source.diffuseColor);
        material.speed = source.speed;

        material.alpha = source.alpha;

        material.id = source.id;

        Tags.AddTagsTo(material, source.tags);
        material.backFaceCulling = source.backFaceCulling;
        material.wireframe = source.wireframe;

        if (source._diffuseTexture) {
            material._diffuseTexture = Texture.Parse(source._diffuseTexture, scene, rootUrl);
        }

        if (source._distortionTexture) {
            material._distortionTexture = Texture.Parse(source._distortionTexture, scene, rootUrl);
        }

        if (source._opacityTexture) {
            material._opacityTexture = Texture.Parse(source._opacityTexture, scene, rootUrl);
        }

        return material;
    }
}

_TypeStore.RegisteredTypes["BABYLON.FireMaterial"] = FireMaterial;